import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import HelpIcon from '@material-ui/icons/Help';
import { Link as RouterLink } from 'react-router-dom';
import {
  configApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';

/* ─── Types ─── */

interface DeptInfo {
  code: string;
  name: string;
  keycloakGroup: string;
  namespaces: { prd: string; dev: string };
}

interface DeptService {
  name: string;
  kind: string;
  type: string;
  namespace: string;
  system: string;
  lifecycle: string;
  owner: string;
  entityRef: string;
}

interface ArgoApp {
  name: string;
  syncStatus: string;
  healthStatus: string;
  revision: string;
}

interface PipelineInfo {
  serviceName: string;
  status: string;
  ref: string;
  finishedAt: string;
  webUrl: string;
}

interface NamespaceMetrics {
  namespace: string;
  cpuUsage: number;
  cpuRequest: number;
  memUsage: number;
  memRequest: number;
  podCount: number;
}

interface QuotaItem {
  resource: string;
  used: number;
  hard: number;
}

/* ─── Styles ─── */

const useStyles = makeStyles(theme => ({
  deptHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  deptTitle: {
    fontWeight: 700,
    fontSize: '1.1rem',
  },
  deptCode: {
    color: theme.palette.text.secondary,
    fontWeight: 400,
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  serviceRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  statusIcon: {
    verticalAlign: 'middle',
    marginRight: 4,
    fontSize: '1rem',
  },
  healthy: { color: '#4caf50' },
  degraded: { color: '#f44336' },
  progressing: { color: '#ff9800' },
  unknown: { color: '#9e9e9e' },
  progressBar: {
    height: 8,
    borderRadius: 4,
  },
  metricCard: {
    textAlign: 'center',
    padding: theme.spacing(1),
  },
  metricValue: {
    fontSize: '1.4rem',
    fontWeight: 700,
  },
  metricLabel: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  emptyMsg: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    padding: theme.spacing(3),
  },
  link: {
    textDecoration: 'none',
    color: theme.palette.primary.main,
    '&:hover': { textDecoration: 'underline' },
  },
}));

/* ─── Helpers ─── */

function HealthIcon({
  status,
  classes,
}: {
  status: string;
  classes: ReturnType<typeof useStyles>;
}) {
  switch (status) {
    case 'Healthy':
      return <CheckCircleIcon className={`${classes.statusIcon} ${classes.healthy}`} />;
    case 'Degraded':
      return <ErrorIcon className={`${classes.statusIcon} ${classes.degraded}`} />;
    case 'Progressing':
      return <WarningIcon className={`${classes.statusIcon} ${classes.progressing}`} />;
    default:
      return <HelpIcon className={`${classes.statusIcon} ${classes.unknown}`} />;
  }
}

function SyncChip({ status }: { status: string }) {
  const colorMap: Record<string, 'primary' | 'secondary' | 'default'> = {
    Synced: 'primary',
    OutOfSync: 'secondary',
  };
  return (
    <Chip
      label={status}
      size="small"
      color={colorMap[status] || 'default'}
      variant="outlined"
    />
  );
}

// ResourceQuota 값 포맷 (resource 종류별 단위)
function fmtQuota(resource: string, v: number): string {
  if (resource.includes('cpu')) {
    return v < 1 ? `${(v * 1000).toFixed(0)}m` : `${v}`;
  }
  if (resource.includes('memory')) {
    return v >= 1024 ** 3
      ? `${(v / 1024 ** 3).toFixed(1)}Gi`
      : `${(v / 1024 ** 2).toFixed(0)}Mi`;
  }
  return `${v}`;
}

function quotaColor(pct: number): string {
  if (pct >= 95) return '#f44336';
  if (pct >= 80) return '#ff9800';
  return '#4caf50';
}

/* ─── Hook: resolve user departments ─── */

function useUserDepts(): DeptInfo[] {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [depts, setDepts] = useState<DeptInfo[]>([]);

  useEffect(() => {
    async function resolve() {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const refs = (identity.ownershipEntityRefs ?? []).map(r =>
          r.toLowerCase(),
        );

        // {dept}-hq 패턴에서 부서 코드 추출
        const deptCodes = new Set<string>();
        const hqPattern = /^group:default\/([a-z0-9]+)-hq$/;
        for (const ref of refs) {
          const m = hqPattern.exec(ref);
          if (m) deptCodes.add(m[1]);
        }

        // 일반 그룹에서도 부서 추출 (developer가 dept-hq가 아닌 하위 그룹 소속인 경우)
        // group:default/t01 → t01
        for (const ref of refs) {
          if (ref.startsWith('group:default/') && !ref.endsWith('-hq') && ref !== 'group:default/platform-admins') {
            const groupName = ref.replace('group:default/', '');
            // 부서 코드인지 확인 (System 엔티티 존재 여부)
            try {
              const system = await catalogApi.getEntityByRef(
                `system:default/${groupName}-system`,
              );
              if (system) deptCodes.add(groupName);
            } catch {
              // not a dept
            }
          }
        }

        // 부서별 정보 조회 (catalog System 엔티티에서)
        const results: DeptInfo[] = [];
        for (const code of deptCodes) {
          // namespace 규칙: svc-{code} / svc-{code}-dev
          results.push({
            code,
            name: code.toUpperCase(),
            keycloakGroup: `${code}-hq`,
            namespaces: {
              prd: `svc-${code}`,
              dev: `svc-${code}-dev`,
            },
          });
        }

        setDepts(results);
      } catch {
        // ignore
      }
    }
    resolve();
  }, [identityApi, catalogApi]);

  return depts;
}

/* ─── Main Component ─── */

export function DeptDashboardCard({ dept }: { dept: DeptInfo }) {
  const classes = useStyles();
  const fetchApi = useApi(fetchApiRef);
  const catalogApi = useApi(catalogApiRef);
  const configApi = useApi(configApiRef);

  const [services, setServices] = useState<DeptService[]>([]);
  const [argoApps, setArgoApps] = useState<ArgoApp[]>([]);
  const [metrics, setMetrics] = useState<NamespaceMetrics | null>(null);
  const [quota, setQuota] = useState<QuotaItem[]>([]);
  const [pipelines, setPipelines] = useState<PipelineInfo[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchDeptData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Catalog에서 부서 서비스 조회 (namespace 기반)
      const [svcResp, webResp, dbResp] = await Promise.all([
        catalogApi.getEntities({
          filter: { kind: 'component', 'metadata.namespace': 'default' },
        }),
        catalogApi.getEntities({
          filter: { kind: 'component', 'spec.type': 'website' },
        }),
        catalogApi.getEntities({
          filter: { kind: 'resource', 'spec.type': 'database' },
        }),
      ]);

      const allEntities = [
        ...svcResp.items,
        ...webResp.items,
        ...dbResp.items,
      ];

      // 부서 소속 필터: annotation backstage.io/kubernetes-namespace 또는 system에 부서코드 포함
      const deptServices = allEntities
        .filter((e: Entity) => {
          const ns =
            e.metadata?.annotations?.['backstage.io/kubernetes-namespace'] || '';
          const sys = (e.spec as any)?.system || '';
          const owner = (e.spec as any)?.owner || '';
          return (
            ns.includes(`svc-${dept.code}`) ||
            sys.includes(dept.code) ||
            owner.includes(`${dept.code}-hq`)
          );
        })
        .map((e: Entity) => ({
          name: e.metadata.name,
          kind: e.kind,
          type: (e.spec as any)?.type || '',
          namespace:
            e.metadata?.annotations?.['backstage.io/kubernetes-namespace'] || '',
          system: (e.spec as any)?.system || '',
          lifecycle: (e.spec as any)?.lifecycle || '',
          owner: (e.spec as any)?.owner || '',
          entityRef: `${e.kind.toLowerCase()}:${e.metadata.namespace || 'default'}/${e.metadata.name}`,
        }));

      setServices(deptServices);

      // 2. ArgoCD 앱 상태 (부서 서비스명 매칭)
      try {
        const argoRes = await fetchApi.fetch(
          `/api/proxy/argocd/api/v1/applications?fields=items.metadata.name,items.status.sync.status,items.status.health.status,items.status.sync.revision`,
        );
        if (argoRes.ok) {
          const argoData = await argoRes.json();
          const apps: ArgoApp[] = (argoData.items || [])
            .filter((app: any) => {
              const name = app.metadata?.name || '';
              return deptServices.some(
                s =>
                  name === s.name ||
                  name === `dev-${s.name}`,
              );
            })
            .map((app: any) => ({
              name: app.metadata?.name,
              syncStatus: app.status?.sync?.status || 'Unknown',
              healthStatus: app.status?.health?.status || 'Unknown',
              revision: (app.status?.sync?.revision || '').slice(0, 8),
            }));
          setArgoApps(apps);
        }
      } catch {
        // ArgoCD 미응답
      }

      // 3. Prometheus 네임스페이스 메트릭
      try {
        const ns = dept.namespaces.dev;
        const cpuQuery = `sum(rate(container_cpu_usage_seconds_total{namespace="${ns}",container!=""}[5m]))`;
        const cpuReqQuery = `sum(kube_pod_container_resource_requests{namespace="${ns}",resource="cpu"})`;
        const memQuery = `sum(container_memory_working_set_bytes{namespace="${ns}",container!=""})`;
        const memReqQuery = `sum(kube_pod_container_resource_requests{namespace="${ns}",resource="memory"})`;
        const podQuery = `count(kube_pod_status_phase{namespace="${ns}",phase="Running"})`;

        const promBase = '/api/proxy/prometheus/api/v1/query';
        const [cpuRes, cpuReqRes, memRes, memReqRes, podRes] =
          await Promise.all([
            fetchApi.fetch(`${promBase}?query=${encodeURIComponent(cpuQuery)}`),
            fetchApi.fetch(`${promBase}?query=${encodeURIComponent(cpuReqQuery)}`),
            fetchApi.fetch(`${promBase}?query=${encodeURIComponent(memQuery)}`),
            fetchApi.fetch(`${promBase}?query=${encodeURIComponent(memReqQuery)}`),
            fetchApi.fetch(`${promBase}?query=${encodeURIComponent(podQuery)}`),
          ]);

        const getValue = async (res: Response) => {
          if (!res.ok) return 0;
          const d = await res.json();
          return parseFloat(d?.data?.result?.[0]?.value?.[1] || '0');
        };

        const [cpu, cpuReq, mem, memReq, pods] = await Promise.all([
          getValue(cpuRes),
          getValue(cpuReqRes),
          getValue(memRes),
          getValue(memReqRes),
          getValue(podRes),
        ]);

        setMetrics({
          namespace: ns,
          cpuUsage: cpu,
          cpuRequest: cpuReq,
          memUsage: mem,
          memRequest: memReq,
          podCount: pods,
        });
      } catch {
        // Prometheus 미응답
      }

      // 3b. ResourceQuota 사용률 (kube_resourcequota: used/hard, resource 라벨별)
      try {
        const ns = dept.namespaces.dev;
        const promBase = '/api/proxy/prometheus/api/v1/query';
        const [usedRes, hardRes] = await Promise.all([
          fetchApi.fetch(
            `${promBase}?query=${encodeURIComponent(`kube_resourcequota{namespace="${ns}",type="used"}`)}`,
          ),
          fetchApi.fetch(
            `${promBase}?query=${encodeURIComponent(`kube_resourcequota{namespace="${ns}",type="hard"}`)}`,
          ),
        ]);
        const parseSeries = async (res: Response) => {
          const map: Record<string, number> = {};
          if (!res.ok) return map;
          const d = await res.json();
          (d?.data?.result || []).forEach((r: any) => {
            const key = r.metric?.resource;
            if (key) map[key] = parseFloat(r.value?.[1] || '0');
          });
          return map;
        };
        const [usedMap, hardMap] = await Promise.all([
          parseSeries(usedRes),
          parseSeries(hardRes),
        ]);
        const items: QuotaItem[] = Object.keys(hardMap)
          .map(resource => ({
            resource,
            used: usedMap[resource] ?? 0,
            hard: hardMap[resource],
          }))
          .sort((a, b) => a.resource.localeCompare(b.resource));
        setQuota(items);
      } catch {
        // ResourceQuota 미설정/미응답
      }

      // 4. 최근 파이프라인 (GitLab proxy)
      try {
        const gitlabProxy = '/api/proxy/gitlab/api/v4';
        // 부서 그룹의 프로젝트 파이프라인
        const projRes = await fetchApi.fetch(
          `${gitlabProxy}/groups/${dept.code}/projects?per_page=20&include_subgroups=true`,
        );
        if (projRes.ok) {
          const projects = await projRes.json();
          const pipelinePromises = projects.slice(0, 10).map(async (p: any) => {
            try {
              const pipRes = await fetchApi.fetch(
                `${gitlabProxy}/projects/${p.id}/pipelines?per_page=1`,
              );
              if (!pipRes.ok) return null;
              const pips = await pipRes.json();
              if (pips.length === 0) return null;
              return {
                serviceName: p.name,
                status: pips[0].status,
                ref: pips[0].ref,
                finishedAt: pips[0].updated_at || '',
                webUrl: pips[0].web_url || '',
              } as PipelineInfo;
            } catch {
              return null;
            }
          });
          const results = (await Promise.all(pipelinePromises)).filter(
            Boolean,
          ) as PipelineInfo[];
          setPipelines(results);
        }
      } catch {
        // GitLab 미응답
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [dept, catalogApi, fetchApi, configApi]);

  useEffect(() => {
    fetchDeptData();
    const interval = setInterval(fetchDeptData, 60000);
    return () => clearInterval(interval);
  }, [fetchDeptData]);

  const cpuPct =
    metrics && metrics.cpuRequest > 0
      ? (metrics.cpuUsage / metrics.cpuRequest) * 100
      : 0;
  const memPct =
    metrics && metrics.memRequest > 0
      ? (metrics.memUsage / metrics.memRequest) * 100
      : 0;

  return (
    <Card variant="outlined">
      <CardContent>
        {/* Header */}
        <Box className={classes.deptHeader}>
          <Typography className={classes.deptTitle}>
            {dept.name}{' '}
            <span className={classes.deptCode}>({dept.code})</span>
          </Typography>
          <Tooltip title="새로고침">
            <IconButton size="small" onClick={fetchDeptData}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {loading && <LinearProgress />}

        {/* Resource Metrics */}
        {metrics && (
          <>
            <Typography className={classes.sectionTitle}>
              리소스 사용량 ({metrics.namespace})
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box className={classes.metricCard}>
                  <Typography className={classes.metricValue}>
                    {metrics.podCount}
                  </Typography>
                  <Typography className={classes.metricLabel}>Pods</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box className={classes.metricCard}>
                  <Typography className={classes.metricValue}>
                    {(cpuPct).toFixed(0)}%
                  </Typography>
                  <Typography className={classes.metricLabel}>
                    CPU ({(metrics.cpuUsage * 1000).toFixed(0)}m / {(metrics.cpuRequest * 1000).toFixed(0)}m)
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(cpuPct, 100)}
                    className={classes.progressBar}
                  />
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box className={classes.metricCard}>
                  <Typography className={classes.metricValue}>
                    {(memPct).toFixed(0)}%
                  </Typography>
                  <Typography className={classes.metricLabel}>
                    Memory ({(metrics.memUsage / 1024 / 1024).toFixed(0)}Mi / {(metrics.memRequest / 1024 / 1024).toFixed(0)}Mi)
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(memPct, 100)}
                    className={classes.progressBar}
                  />
                </Box>
              </Grid>
            </Grid>
          </>
        )}

        {/* ResourceQuota 사용률 */}
        {quota.length > 0 && (
          <>
            <Typography className={classes.sectionTitle}>
              ResourceQuota 사용률
            </Typography>
            {quota.map(q => {
              const pct = q.hard > 0 ? (q.used / q.hard) * 100 : 0;
              const color = quotaColor(pct);
              return (
                <Box key={q.resource} mb={1}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption">{q.resource}</Typography>
                    <Typography
                      variant="caption"
                      style={{ color, fontWeight: 600 }}
                    >
                      {pct.toFixed(0)}% ({fmtQuota(q.resource, q.used)} /{' '}
                      {fmtQuota(q.resource, q.hard)})
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(pct, 100)}
                    className={classes.progressBar}
                  />
                </Box>
              );
            })}
          </>
        )}

        {/* Services Table */}
        <Typography className={classes.sectionTitle}>
          서비스 ({services.length})
        </Typography>
        {services.length === 0 && !loading ? (
          <Typography className={classes.emptyMsg}>
            등록된 서비스가 없습니다
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>서비스</TableCell>
                  <TableCell>타입</TableCell>
                  <TableCell>ArgoCD</TableCell>
                  <TableCell>Health</TableCell>
                  <TableCell>Revision</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services.map(svc => {
                  const argo = argoApps.find(
                    a => a.name === svc.name || a.name === `dev-${svc.name}`,
                  );
                  return (
                    <TableRow key={svc.name} className={classes.serviceRow}>
                      <TableCell>
                        <RouterLink
                          to={`/catalog/default/${svc.kind.toLowerCase()}/${svc.name}`}
                          className={classes.link}
                        >
                          {svc.name}
                        </RouterLink>
                      </TableCell>
                      <TableCell>
                        <Chip label={svc.type} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {argo ? <SyncChip status={argo.syncStatus} /> : '-'}
                      </TableCell>
                      <TableCell>
                        {argo ? (
                          <HealthIcon status={argo.healthStatus} classes={classes} />
                        ) : (
                          '-'
                        )}
                        {argo?.healthStatus || ''}
                      </TableCell>
                      <TableCell>
                        <code>{argo?.revision || '-'}</code>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Recent Pipelines */}
        {pipelines.length > 0 && (
          <>
            <Typography className={classes.sectionTitle}>
              최근 파이프라인
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>서비스</TableCell>
                    <TableCell>상태</TableCell>
                    <TableCell>브랜치</TableCell>
                    <TableCell>시간</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pipelines.map(p => (
                    <TableRow key={p.serviceName}>
                      <TableCell>{p.serviceName}</TableCell>
                      <TableCell>
                        <Chip
                          label={p.status}
                          size="small"
                          color={
                            p.status === 'success'
                              ? 'primary'
                              : p.status === 'failed'
                                ? 'secondary'
                                : 'default'
                          }
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{p.ref}</TableCell>
                      <TableCell>
                        {p.finishedAt
                          ? new Date(p.finishedAt).toLocaleString('ko-KR', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── Export: hook for HomePage to use ─── */
export { useUserDepts };
export type { DeptInfo };
